import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useCustomerProfile } from '../hooks/use-customer-profile';
import { useCouponAuth } from '../hooks/use-coupon-auth';
import { useClippedCoupons } from '../hooks/use-clipped-coupons';
import { useLoyaltyCard } from '../hooks/use-loyalty-card';
import { loyaltyLaneClient } from '@/services/api/loyalty-lane-client';
import type { LLApiResponse, Shopper, ShopperUpdateRequest } from '../types/loyalty-lane';
import { useUserStore } from '@/store/useUserStore';
import { TokenStorage } from '../lib/token-storage';
import { normalizePhoneDigits, formatPhoneDisplay, isValidPhone, toE164 } from '../lib/phone';
import { normalizeBirthdayDigits, formatBirthdayDisplay, isValidBirthday, toLLBirthdate, parseLLBirthDate } from '../lib/birthday';
import { IconCake, IconCheck, IconCircleCheck, IconLoader4, IconLogout, IconMail, IconMapPin, IconSettings, IconTag, IconTrash, IconUser } from '@tabler/icons-react';

interface AccountSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type ViewMode = 'overview' | 'edit-profile' | 'settings';

export const AccountSheet = ({ open, onOpenChange }: AccountSheetProps) => {
  const [viewMode, setViewMode] = useState<ViewMode>('overview');
  const [editedProfile, setEditedProfile] = useState<Partial<ShopperUpdateRequest>>({});
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [signOutDialogOpen, setSignOutDialogOpen] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const navigate = useNavigate();
  const { profile, isLoading, error, refetch, updateProfile } = useCustomerProfile();
  const { logout } = useCouponAuth();
  const { data: clippedCoupons } = useClippedCoupons();
  // Fetched from the LL Card endpoint; couponProfile.cardNumber is only
  // populated when the account was created in this browser.
  const { cardNumber } = useLoyaltyCard(open);

  // Reset view mode when sheet closes
  useEffect(() => {
    if (!open) {
      setViewMode('overview');
      setSaveSuccess(false);
      setShowSuccessAlert(false);
      setDeleteError(null);
    }
  }, [open]);

  // Initialize edited profile when profile loads
  useEffect(() => {
    if (profile) {
      setEditedProfile({
        firstname: profile.FirstName || '',
        lastname: profile.LastName || '',
        emailaddress: profile.Email || profile.EmailAddress || '',
        mobilephonenumber: normalizePhoneDigits(profile.MobilePhone ?? profile.HomePhone ?? ''),
        streetaddress1: profile.StreetAddress1 || '',
        city: profile.City || '',
        state: profile.State || '',
        zipcode: profile.Zipcode || '',
        optin: profile.Optin ?? true,
        optintext: profile.OptinText ?? true,
        birthdate: parseLLBirthDate(profile.BirthDate),
      });
    }
  }, [profile]);

  const handleSignOut = async () => {
    await logout();
    onOpenChange(false);
    navigate('/');
  };

  const handleDeleteAccount = async () => {
    setDeleteLoading(true);
    setDeleteError(null);

    try {
      const shopperid = useUserStore.getState().couponProfile?.shopperid;
      if (shopperid) {
        await loyaltyLaneClient.put<LLApiResponse<Shopper>>('Shopper', {
          shopperid,
          enabled: false,
        });
      }

      // Clear auth/session data only — NOT the whole origin (preserves
      // unrelated app state such as cookie-consent choices and saved location).
      TokenStorage.clearAll();
      useUserStore.getState().setCouponProfile(null);

      setDeleteDialogOpen(false);
      onOpenChange(false);
      navigate('/');
    } catch {
      setDeleteError('Failed to delete account. Please try again.');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    setFormError(null);

    if (!isValidPhone(editedProfile.mobilephonenumber)) {
      setFormError('Please enter a valid 10-digit phone number');
      return;
    }

    if (editedProfile.birthdate && !isValidBirthday(editedProfile.birthdate)) {
      setFormError('Please enter a valid birthday (MM/DD/YYYY)');
      return;
    }

    try {
      const payload: ShopperUpdateRequest = {
        ...editedProfile,
        mobilephonenumber: toE164(editedProfile.mobilephonenumber),
      };
      const llBirthdate = toLLBirthdate(editedProfile.birthdate);
      if (llBirthdate) {
        payload.birthdate = llBirthdate;
      } else {
        delete payload.birthdate;
      }

      await updateProfile.mutateAsync(payload);
      setSaveSuccess(true);
      setShowSuccessAlert(true);
      await refetch();

      setTimeout(() => {
        setSaveSuccess(false);
        setViewMode('overview');
      }, 1500);

      setTimeout(() => {
        setShowSuccessAlert(false);
      }, 3000);
    } catch (err) {
      console.error('Failed to save profile:', err);
    }
  };

  const titleCase = (s: string) => s.toLowerCase().replace(/(?:^|\s)\S/g, (c) => c.toUpperCase());
  const displayName = profile?.FirstName ? titleCase(profile.FirstName) : 'Member';
  const clippedCount = clippedCoupons?.length || 0;
  const birthdayDisplay = formatBirthdayDisplay(parseLLBirthDate(profile?.BirthDate));

  const formatAddress = (): string => {
    const parts = [
      profile?.StreetAddress1 ? titleCase(profile.StreetAddress1) : '',
      profile?.City ? titleCase(profile.City) : '',
      profile?.State ? profile.State.toUpperCase() : '',
      profile?.Zipcode || '',
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : 'Not set';
  };

  const renderOverview = () => (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto">
        {showSuccessAlert && (
          <div className="px-6 pt-4">
            <Alert className="bg-green-50 border-green-200 text-green-800">
              <IconCircleCheck className="h-4 w-4 text-green-600" />
              <AlertDescription>Profile updated successfully!</AlertDescription>
            </Alert>
          </div>
        )}

        <div className="px-6 py-4">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
              <IconUser className="h-8 w-8 text-primary" />
            </div>
            <div>
              <h3 className="text-xl font-semibold">
                {displayName} {profile?.LastName ? titleCase(profile.LastName) : ''}
              </h3>
              <p className="text-sm text-muted-foreground">
                {profile?.Email || profile?.EmailAddress || 'No email on file'}
              </p>
              {cardNumber && (
                <p className="text-sm text-muted-foreground">Card #: {cardNumber}</p>
              )}
            </div>
          </div>

          <div className="mb-6">
            <Link
              to="/coupons?tab=clipped"
              onClick={() => onOpenChange(false)}
              className="block p-4 rounded-lg border bg-card hover:bg-accent transition-colors"
            >
              <div className="flex items-center gap-3">
                <IconTag className="h-5 w-5 text-primary" />
                <div>
                  <p className="text-2xl font-bold">{clippedCount}</p>
                  <p className="text-xs text-muted-foreground">Saved Offers</p>
                </div>
              </div>
            </Link>
          </div>

          <Separator className="my-4" />

          <div className="space-y-3">
            <h4 className="text-sm font-medium text-muted-foreground mb-3">Contact Information</h4>

            <div className="flex items-center gap-3">
              <IconMail className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span className={`text-sm ${!(profile?.Email || profile?.EmailAddress) ? 'text-muted-foreground italic' : ''}`}>
                {profile?.Email || profile?.EmailAddress || 'Not set'}
              </span>
            </div>

            <div className="flex items-center gap-3">
              <IconMapPin className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span className={`text-sm ${!profile?.StreetAddress1 ? 'text-muted-foreground italic' : ''}`}>
                {formatAddress()}
              </span>
            </div>

            <div className="flex items-center gap-3">
              <IconCake className="h-4 w-4 text-muted-foreground" />
              <span className={`text-sm ${!birthdayDisplay ? 'text-muted-foreground italic' : ''}`}>
                {birthdayDisplay || 'Not set'}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t p-4 space-y-2">
        <Button
          variant="outline"
          className="w-full justify-start"
          onClick={() => setViewMode('edit-profile')}
        >
          <IconUser className="h-4 w-4 mr-2" />
          Edit Profile
        </Button>
        <Button
          variant="outline"
          className="w-full justify-start"
          onClick={() => setViewMode('settings')}
        >
          <IconSettings className="h-4 w-4 mr-2" />
          Settings
        </Button>
        <Separator className="my-2" />
        <Button
          variant="ghost"
          className="w-full justify-start text-destructive hover:text-destructive hover:bg-destructive/10"
          onClick={() => setSignOutDialogOpen(true)}
        >
          <IconLogout className="h-4 w-4 mr-2" />
          Sign Out
        </Button>
      </div>
    </div>
  );

  const renderEditProfile = () => (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto px-6 py-4">
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="firstName">First Name</Label>
              <Input
                id="firstName"
                value={editedProfile.firstname || ''}
                onChange={(e) => setEditedProfile({ ...editedProfile, firstname: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName">Last Name</Label>
              <Input
                id="lastName"
                value={editedProfile.lastname || ''}
                onChange={(e) => setEditedProfile({ ...editedProfile, lastname: e.target.value })}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={editedProfile.emailaddress || ''}
              onChange={(e) => setEditedProfile({ ...editedProfile, emailaddress: e.target.value })}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone">Phone Number</Label>
            <div className="flex gap-2">
              <div className="flex items-center px-3 border border-input bg-background rounded-md text-muted-foreground">
                +1
              </div>
              <Input
                id="phone"
                type="tel"
                placeholder="(555) 123-4567"
                value={formatPhoneDisplay(editedProfile.mobilephonenumber)}
                onChange={(e) => setEditedProfile({ ...editedProfile, mobilephonenumber: normalizePhoneDigits(e.target.value) })}
                autoComplete="tel-national"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="birthday">
              Birthday <span className="font-normal text-muted-foreground">(optional)</span>
            </Label>
            <Input
              id="birthday"
              type="text"
              inputMode="numeric"
              placeholder="MM/DD/YYYY"
              value={formatBirthdayDisplay(editedProfile.birthdate)}
              onChange={(e) => setEditedProfile({ ...editedProfile, birthdate: normalizeBirthdayDigits(e.target.value) })}
              autoComplete="bday"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="streetaddress1">Street Address</Label>
            <Input
              id="streetaddress1"
              value={editedProfile.streetaddress1 || ''}
              onChange={(e) => setEditedProfile({ ...editedProfile, streetaddress1: e.target.value })}
              placeholder="123 Main St"
            />
          </div>

          <div className="grid grid-cols-5 gap-2">
            <div className="col-span-2 space-y-2">
              <Label htmlFor="city">City</Label>
              <Input
                id="city"
                value={editedProfile.city || ''}
                onChange={(e) => setEditedProfile({ ...editedProfile, city: e.target.value })}
                placeholder="City"
              />
            </div>
            <div className="col-span-1 space-y-2">
              <Label htmlFor="state">State</Label>
              <Input
                id="state"
                maxLength={2}
                value={editedProfile.state || ''}
                onChange={(e) => setEditedProfile({ ...editedProfile, state: e.target.value.toUpperCase() })}
                placeholder="MO"
              />
            </div>
            <div className="col-span-2 space-y-2">
              <Label htmlFor="zip">ZIP Code</Label>
              <Input
                id="zip"
                type="text"
                maxLength={5}
                placeholder="12345"
                value={editedProfile.zipcode || ''}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '');
                  if (value.length <= 5) {
                    setEditedProfile({ ...editedProfile, zipcode: value });
                  }
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="border-t p-4 space-y-2">
        {formError && (
          <Alert variant="destructive" className="mb-2">
            <AlertDescription>{formError}</AlertDescription>
          </Alert>
        )}
        {updateProfile.isError && (
          <Alert variant="destructive" className="mb-2">
            <AlertDescription>Failed to save changes. Please try again.</AlertDescription>
          </Alert>
        )}
        <div className="flex gap-2">
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => setViewMode('overview')}
            disabled={updateProfile.isPending}
          >
            Cancel
          </Button>
          <Button
            className="flex-1"
            onClick={handleSaveProfile}
            disabled={updateProfile.isPending}
          >
            {updateProfile.isPending ? (
              <>
                <IconLoader4 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : saveSuccess ? (
              <>
                <IconCheck className="h-4 w-4 mr-2" />
                Saved!
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        </div>
      </div>
    </div>
  );

  const renderSettings = () => (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto px-6 py-4">
        <div className="space-y-6">
          <div>
            <h4 className="text-sm font-medium mb-4">Communication Preferences</h4>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="receiveEmail">Email Notifications</Label>
                  <p className="text-xs text-muted-foreground">Receive deals and offers via email</p>
                </div>
                <Switch
                  id="receiveEmail"
                  checked={editedProfile.optin ?? true}
                  onCheckedChange={(checked) => setEditedProfile({ ...editedProfile, optin: checked })}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="receiveSMS">SMS Notifications</Label>
                  <p className="text-xs text-muted-foreground">Receive deals and offers via text</p>
                </div>
                <Switch
                  id="receiveSMS"
                  checked={editedProfile.optintext ?? true}
                  onCheckedChange={(checked) => setEditedProfile({ ...editedProfile, optintext: checked })}
                />
              </div>
            </div>
          </div>

          <Separator />

          <div>
            <h4 className="text-sm font-medium mb-4 text-destructive">Danger Zone</h4>
            <Button
              variant="outline"
              className="w-full justify-start text-destructive border-destructive/30 hover:bg-destructive/10 hover:text-destructive"
              onClick={() => setDeleteDialogOpen(true)}
            >
              <IconTrash className="h-4 w-4 mr-2" />
              Delete Account
            </Button>
            <p className="text-xs text-muted-foreground mt-2">
              This will deactivate your account and clear all local data.
            </p>
          </div>
        </div>
      </div>

      <div className="border-t p-4">
        <Button
          variant="outline"
          className="w-full"
          onClick={() => setViewMode('overview')}
        >
          Back to Account
        </Button>
      </div>
    </div>
  );

  const getTitle = () => {
    switch (viewMode) {
      case 'edit-profile':
        return 'Edit Profile';
      case 'settings':
        return 'Settings';
      default:
        return 'My Account';
    }
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col">
          <SheetHeader className="px-6 pt-6 pb-4 border-b text-left">
            <SheetTitle>{getTitle()}</SheetTitle>
            {viewMode === 'overview' && (
              <SheetDescription>
                Manage your account and preferences
              </SheetDescription>
            )}
          </SheetHeader>

          {isLoading ? (
            <div className="flex-1 px-6 py-4">
              <div className="flex items-center gap-4 mb-6">
                <Skeleton className="w-16 h-16 rounded-full" />
                <div className="space-y-2">
                  <Skeleton className="h-6 w-32" />
                  <Skeleton className="h-4 w-48" />
                </div>
              </div>
              <div className="space-y-4">
                <Skeleton className="h-20 w-full" />
                <Skeleton className="h-20 w-full" />
              </div>
            </div>
          ) : error ? (
            <div className="flex-1 px-6 py-4">
              <Alert variant="destructive">
                <AlertDescription>
                  Failed to load account information. Please try again later.
                </AlertDescription>
              </Alert>
            </div>
          ) : (
            <>
              {viewMode === 'overview' && renderOverview()}
              {viewMode === 'edit-profile' && renderEditProfile()}
              {viewMode === 'settings' && renderSettings()}
            </>
          )}
        </SheetContent>
      </Sheet>

      <AlertDialog open={signOutDialogOpen} onOpenChange={setSignOutDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Sign Out</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to sign out? You will need to sign in again to access your saved offers.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleSignOut}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Sign Out
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Account</AlertDialogTitle>
            <AlertDialogDescription>
              This will deactivate your account and clear all your app data. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {deleteError && (
            <Alert variant="destructive">
              <AlertDescription>{deleteError}</AlertDescription>
            </Alert>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteAccount}
              disabled={deleteLoading}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteLoading ? (
                <>
                  <IconLoader4 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete Account'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
